import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Briefcase, MapPin, Clock, Loader2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import AdminLogin from './AdminLogin';
import AdminHeader from './AdminHeader';
import { useAdminAuth } from '../hooks/useAdminAuth';

import { apiUrl, API_URL } from '../utils/config';
import logger from '../utils/logger';

const CareersAdmin = ({ onBackToHome }) => {
  const { isAuth, isCheckingAuth, handleLogin, handleLogout } = useAdminAuth();
  const [positions, setPositions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('en'); // Language tab
  
  // Form state with multi-language support
  const [formData, setFormData] = useState({
    title_en: '',
    title_tr: '',
    department_en: '',
    department_tr: '',
    location_en: 'Remote',
    location_tr: 'Uzaktan',
    type_en: 'Full-time',
    type_tr: 'Tam zamanlı',
    description_en: '',
    description_tr: '',
    requirements_en: [''],
    requirements_tr: ['']
  });

  useEffect(() => {
    if (isAuth) {
      fetchPositions();
    }
  }, [isAuth]);

  const fetchPositions = async () => {
    try {
      const response = await fetch(apiUrl("careers/positions/admin"));
      if (response.ok) {
        const data = await response.json();
        setPositions(data);
      }
    } catch (error) {
      logger.error('Error fetching positions:', error);
      toast.error('Failed to load job positions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRequirementChange = (index, value, lang = 'en') => {
    const field = `requirements_${lang}`;
    const newRequirements = [...formData[field]];
    newRequirements[index] = value;
    setFormData(prev => ({ ...prev, [field]: newRequirements }));
  };

  const addRequirement = (lang = 'en') => {
    const field = `requirements_${lang}`;
    setFormData(prev => ({ ...prev, [field]: [...prev[field], ''] }));
  };

  const removeRequirement = (index, lang = 'en') => {
    const field = `requirements_${lang}`;
    if (formData[field].length > 1) {
      const newRequirements = formData[field].filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, [field]: newRequirements }));
    }
  };

  const resetForm = () => {
    setFormData({
      title_en: '',
      title_tr: '',
      department_en: '',
      department_tr: '',
      location_en: 'Remote',
      location_tr: 'Uzaktan',
      type_en: 'Full-time',
      type_tr: 'Tam zamanlı',
      description_en: '',
      description_tr: '',
      requirements_en: [''],
      requirements_tr: ['']
    });
    setActiveTab('en');
    setShowAddForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Filter out empty requirements
      const submitData = {
        ...formData,
        requirements_en: formData.requirements_en.filter(r => r.trim() !== ''),
        requirements_tr: formData.requirements_tr.filter(r => r.trim() !== '')
      };
      
      const response = await fetch(apiUrl("careers/positions"), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        toast.success('Job position created successfully');
        resetForm();
        fetchPositions();
      } else {
        toast.error('Failed to create job position');
      }
    } catch (error) {
      logger.error('Error creating position:', error);
      toast.error('Failed to create job position');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (positionId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) {
      return;
    }

    try {
      const response = await fetch(apiUrl("careers/positions/${positionId}"), {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Job position deleted');
        fetchPositions();
      } else {
        toast.error('Failed to delete job position');
      }
    } catch (error) {
      logger.error('Error deleting position:', error);
      toast.error('Failed to delete job position');
    }
  };

  // Show loading while checking auth
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Login screen
  if (!isAuth) {
    return <AdminLogin onLogin={handleLogin} title="Careers Admin" />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <AdminHeader
        title="Careers Admin"
        onBackToHome={onBackToHome}
        onLogout={handleLogout}
        onAddNew={() => setShowAddForm(true)}
        addNewLabel="Add Position"
      />

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        {/* Stats */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Overview</h2>
          <p className="text-3xl font-bold text-blue-600">{positions.length}</p>
          <p className="text-gray-500">Active Job Positions</p>
        </div>

        {/* Positions List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : positions.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Job Positions</h3>
            <p className="text-gray-500 mb-6">Get started by adding your first job position</p>
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Position
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {positions.map((position) => (
              <div
                key={position.id}
                className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow"
                data-testid={`position-card-${position.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-xl font-bold text-gray-900">{position.title_en}</h3>
                      {position.title_tr && (
                        <span className="inline-flex items-center px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded">
                          🇹🇷 TR
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-4">
                      <span className="flex items-center gap-1">
                        <Briefcase className="w-4 h-4" />
                        {position.department_en}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {position.location_en}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {position.type_en}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-4">{position.description_en}</p>
                    {position.requirements_en && position.requirements_en.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-700 mb-2">Requirements:</h4>
                        <ul className="list-disc list-inside text-gray-600 space-y-1">
                          {position.requirements_en.map((req, index) => (
                            <li key={req}>{req}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(position.id, position.title_en)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-position-${position.id}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Position Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Add New Position</h2>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Language Tabs */}
              <div className="flex gap-2 border-b border-gray-200 pb-4">
                <button
                  type="button"
                  onClick={() => setActiveTab('en')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'en' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  🇬🇧 English
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('tr')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'tr' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  🇹🇷 Turkish
                </button>
                <span className="ml-auto text-sm text-gray-500 self-center">
                  {activeTab === 'en' ? 'Required fields' : 'Optional - falls back to English'}
                </span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Job Title {activeTab === 'en' && '*'}
                </label>
                <Input
                  name={`title_${activeTab}`}
                  value={formData[`title_${activeTab}`]}
                  onChange={handleInputChange}
                  placeholder={activeTab === 'en' ? 'e.g., Remote Coach' : 'örn., Uzaktan Koç'}
                  required={activeTab === 'en'}
                  data-testid="job-title-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Department {activeTab === 'en' && '*'}
                  </label>
                  <Input
                    name={`department_${activeTab}`}
                    value={formData[`department_${activeTab}`]}
                    onChange={handleInputChange}
                    placeholder={activeTab === 'en' ? 'e.g., Coaching' : 'örn., Koçluk'}
                    required={activeTab === 'en'}
                    data-testid="department-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location {activeTab === 'en' && '*'}
                  </label>
                  <Input
                    name={`location_${activeTab}`}
                    value={formData[`location_${activeTab}`]}
                    onChange={handleInputChange}
                    placeholder={activeTab === 'en' ? 'e.g., Remote' : 'örn., Uzaktan'}
                    required={activeTab === 'en'}
                    data-testid="location-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Employment Type {activeTab === 'en' && '*'}
                </label>
                <Input
                  name={`type_${activeTab}`}
                  value={formData[`type_${activeTab}`]}
                  onChange={handleInputChange}
                  placeholder={activeTab === 'en' ? 'e.g., Full-time' : 'örn., Tam zamanlı'}
                  required={activeTab === 'en'}
                  data-testid="type-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Job Description {activeTab === 'en' && '*'}
                </label>
                <textarea
                  name={`description_${activeTab}`}
                  value={formData[`description_${activeTab}`]}
                  onChange={handleInputChange}
                  placeholder={activeTab === 'en' ? 'Describe the role and responsibilities...' : 'Rol ve sorumlulukları açıklayın...'}
                  rows={4}
                  required={activeTab === 'en'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="description-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Requirements ({activeTab === 'en' ? 'English' : 'Turkish'})
                </label>
                <div className="space-y-3">
                  {formData[`requirements_${activeTab}`].map((req, index) => (
                    <div key={`req-${index}`} className="flex gap-2">
                      <Input
                        value={req}
                        onChange={(e) => handleRequirementChange(index, e.target.value, activeTab)}
                        placeholder={activeTab === 'en' ? `Requirement ${index + 1}` : `Gereksinim ${index + 1}`}
                        data-testid={`requirement-input-${index}`}
                      />
                      {formData[`requirements_${activeTab}`].length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeRequirement(index, activeTab)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => addRequirement(activeTab)}
                    className="w-full"
                    data-testid="add-requirement-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {activeTab === 'en' ? 'Add Requirement' : 'Gereksinim Ekle'}
                  </Button>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetForm}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="submit-position-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Position'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareersAdmin;
